import { useEffect, useMemo, useState, useCallback } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from "@/components/ui/dialog";
import {
  TrendingUp,
  TrendingDown,
  DollarSign,
  Percent,
  Search,
  RefreshCw,
  Loader2,
  AlertTriangle,
  CheckCircle2,
  ChevronLeft,
  ChevronRight,
  Edit3,
  Sparkles,
  Package,
  ArrowUpDown,
  Filter,
  Download,
  Palette,
  Eye,
  Save,
  X,
  Zap,
  BarChart3,
} from "lucide-react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const marginColors = {
  negative: "bg-red-500",
  low: "bg-orange-500",
  medium: "bg-yellow-500",
  good: "bg-emerald-500",
  excellent: "bg-green-600",
};

const getMarginColor = (margin) => {
  if (margin === null || margin === undefined) return "bg-gray-400";
  if (margin < 0) return marginColors.negative;
  if (margin < 10) return marginColors.low;
  if (margin < 20) return marginColors.medium;
  if (margin < 30) return marginColors.good;
  return marginColors.excellent;
};

const getMarginLabel = (margin) => {
  if (margin === null || margin === undefined) return "No Data";
  if (margin < 0) return "Loss";
  if (margin < 10) return "Low";
  if (margin < 20) return "Medium";
  if (margin < 30) return "Good";
  return "Excellent";
};

const formatCurrency = (value) => {
  if (value === null || value === undefined) return "-";
  return `£${value.toFixed(2)}`;
};

export const ProfitabilityManager = () => {
  const [report, setReport] = useState(null);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [page, setPage] = useState(1);
  const [limit] = useState(25);
  const [sortBy, setSortBy] = useState("profit_margin");
  const [sortOrder, setSortOrder] = useState("asc");
  const [lowMarginOnly, setLowMarginOnly] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const [selectedCollection, setSelectedCollection] = useState("");
  const [selectedCategory, setSelectedCategory] = useState("");
  
  // Migration state
  const [migrating, setMigrating] = useState(false);
  const [migrationResult, setMigrationResult] = useState(null);
  
  // Price editing state
  const [editingProduct, setEditingProduct] = useState(null);
  const [newPrice, setNewPrice] = useState("");
  const [updating, setUpdating] = useState(false);
  
  // Batch edit state
  const [selectedProducts, setSelectedProducts] = useState([]);
  const [batchEditOpen, setBatchEditOpen] = useState(false);
  const [batchPriceAdjustment, setBatchPriceAdjustment] = useState("");
  const [batchAdjustmentType, setBatchAdjustmentType] = useState("percentage");
  
  // AI Color detection state
  const [detectingColor, setDetectingColor] = useState(null);
  const [batchDetecting, setBatchDetecting] = useState(false);
  
  // Bulk AI color detection state
  const [bulkColorJobId, setBulkColorJobId] = useState(null);
  const [bulkColorStatus, setBulkColorStatus] = useState(null);
  const [missingColorsCount, setMissingColorsCount] = useState(0);
  const [showBulkColorDialog, setShowBulkColorDialog] = useState(false);

  const fetchReport = useCallback(async (silent = false) => {
    if (!silent) {
      setRefreshing(true);
    }
    try {
      const params = new URLSearchParams();
      params.append("page", page.toString());
      params.append("limit", limit.toString());
      params.append("sort_by", sortBy);
      params.append("sort_order", sortOrder);
      if (lowMarginOnly) params.append("low_margin_only", "true");
      if (searchTerm) params.append("search", searchTerm);
      if (selectedCollection) params.append("collection", selectedCollection);
      if (selectedCategory) params.append("category", selectedCategory);
      
      const response = await axios.get(`${API}/admin/profitability/report?${params}`);
      setReport(response.data);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to fetch profitability report");
    }
    if (!silent) {
      setRefreshing(false);
      setLoading(false);
    }
  }, [page, limit, sortBy, sortOrder, lowMarginOnly, searchTerm, selectedCollection, selectedCategory]);

  useEffect(() => {
    fetchReport();
  }, [fetchReport]);

  // Fetch missing colors count
  const fetchMissingColorsCount = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/admin/profitability/missing-colors-count`);
      setMissingColorsCount(response.data.missing_colors_count);
    } catch (err) {
      console.error("Failed to fetch missing colors count:", err);
    }
  }, []);

  useEffect(() => {
    fetchMissingColorsCount();
  }, [fetchMissingColorsCount]);

  // Poll bulk color job status
  useEffect(() => {
    if (!bulkColorJobId) return;
    
    const pollStatus = async () => {
      try {
        const response = await axios.get(`${API}/admin/profitability/bulk-detect-colors/status/${bulkColorJobId}`);
        setBulkColorStatus(response.data);
        
        if (response.data.status === "completed" || response.data.status === "failed") {
          toast.success(`Bulk color detection complete: ${response.data.successful} successful, ${response.data.failed} failed`);
          fetchReport();
          fetchMissingColorsCount();
        }
      } catch (err) {
        console.error("Failed to poll bulk color status:", err);
      }
    };
    
    // Poll every 2 seconds while job is running
    const interval = setInterval(() => {
      if (bulkColorStatus?.status === "completed" || bulkColorStatus?.status === "failed") {
        clearInterval(interval);
        return;
      }
      pollStatus();
    }, 2000);
    
    // Initial poll
    pollStatus();
    
    return () => clearInterval(interval);
  }, [bulkColorJobId, bulkColorStatus?.status, fetchReport, fetchMissingColorsCount]);

  const startBulkColorDetection = async () => {
    try {
      const response = await axios.post(`${API}/admin/profitability/bulk-detect-colors/start`);
      if (response.data.job_id) {
        setBulkColorJobId(response.data.job_id);
        setBulkColorStatus({
          status: "pending",
          total: response.data.total,
          processed: 0,
          successful: 0,
          failed: 0,
          progress_percent: 0
        });
        toast.success(`Started processing ${response.data.total} products`);
        setShowBulkColorDialog(true);
      } else {
        toast.info(response.data.message || "No products to process");
      }
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to start bulk detection");
    }
  };

  const runMigration = async () => {
    setMigrating(true);
    try {
      const response = await axios.post(`${API}/admin/profitability/migrate`);
      setMigrationResult(response.data);
      toast.success(response.data.message);
      fetchReport();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Migration failed");
    }
    setMigrating(false);
  };

  const handlePriceUpdate = async () => {
    if (!editingProduct || !newPrice) return;
    
    setUpdating(true);
    try {
      const response = await axios.put(
        `${API}/admin/profitability/update-price/${editingProduct.id}`,
        null,
        { params: { new_sale_price: parseFloat(newPrice) } }
      );
      toast.success(`Updated ${editingProduct.name} to £${parseFloat(newPrice).toFixed(2)}`);
      setEditingProduct(null);
      setNewPrice("");
      fetchReport();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to update price");
    }
    setUpdating(false);
  };

  const handleBatchPriceUpdate = async () => {
    if (selectedProducts.length === 0 || !batchPriceAdjustment) return;
    
    setUpdating(true);
    try {
      const updates = selectedProducts.map(product => {
        let newSalePrice = product.sale_price || 0;
        const adjustment = parseFloat(batchPriceAdjustment);
        
        if (batchAdjustmentType === "percentage") {
          newSalePrice = newSalePrice * (1 + adjustment / 100);
        } else if (batchAdjustmentType === "fixed") {
          newSalePrice = newSalePrice + adjustment;
        } else if (batchAdjustmentType === "set_margin") {
          // Set price to achieve target margin
          const cost = product.cost_of_good || 0;
          if (cost > 0) {
            newSalePrice = cost / (1 - adjustment / 100);
          }
        }
        
        return {
          product_id: product.id,
          new_sale_price: Math.max(0, Math.round(newSalePrice * 100) / 100)
        };
      });
      
      const response = await axios.post(`${API}/admin/profitability/batch-update-prices`, { updates });
      toast.success(`Updated ${response.data.updated} products`);
      setBatchEditOpen(false);
      setSelectedProducts([]);
      setBatchPriceAdjustment("");
      fetchReport();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Batch update failed");
    }
    setUpdating(false);
  };

  const detectProductColor = async (product) => {
    setDetectingColor(product.id);
    try {
      const response = await axios.post(`${API}/admin/profitability/detect-color`, {
        product_id: product.id
      });
      toast.success(`Detected color: ${response.data.detected_color}`);
      fetchReport(true);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Color detection failed");
    }
    setDetectingColor(null);
  };

  const batchDetectColors = async () => {
    if (selectedProducts.length === 0) return;
    
    setBatchDetecting(true);
    try {
      const response = await axios.post(`${API}/admin/profitability/batch-detect-colors`, 
        selectedProducts.map(p => p.id)
      );
      toast.success(`Detected colors for ${response.data.processed} products`);
      setSelectedProducts([]);
      fetchReport();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Batch color detection failed");
    }
    setBatchDetecting(false);
  };

  const toggleProductSelection = (product) => {
    setSelectedProducts(prev => {
      const exists = prev.find(p => p.id === product.id);
      if (exists) {
        return prev.filter(p => p.id !== product.id);
      }
      return [...prev, product];
    });
  };

  const toggleAllVisible = () => {
    const currentProducts = report?.products || [];
    const allSelected = currentProducts.every(p => selectedProducts.find(sp => sp.id === p.id));
    
    if (allSelected) {
      setSelectedProducts(prev => prev.filter(p => !currentProducts.find(cp => cp.id === p.id)));
    } else {
      setSelectedProducts(prev => {
        const newSelection = [...prev];
        currentProducts.forEach(p => {
          if (!newSelection.find(sp => sp.id === p.id)) {
            newSelection.push(p);
          }
        });
        return newSelection;
      });
    }
  };

  const exportToCSV = () => {
    const products = report?.products || [];
    const headers = ["SKU", "Name", "Collection", "Category", "Sale Price", "Cost", "Profit", "Margin %", "Finish"];
    const rows = products.map(p => [
      p.sku || "",
      `"${(p.name || "").replace(/"/g, '""')}"`,
      `"${(p.collection_name || "").replace(/"/g, '""')}"`,
      p.category || "",
      p.sale_price || "",
      p.cost_of_good || "",
      p.profit_amount || "",
      p.profit_margin || "",
      `"${(p.finish || "").replace(/"/g, '""')}"`
    ]);
    
    const csv = [headers.join(","), ...rows.map(r => r.join(","))].join("\n");
    const blob = new Blob([csv], { type: "text/csv" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `profitability-report-${new Date().toISOString().split("T")[0]}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  const summary = report?.summary || {};
  const products = report?.products || [];
  const filters = report?.filters || {};

  if (loading) {
    return (
      <div className="flex flex-col items-center justify-center h-96 gap-4" data-testid="profitability-loading">
        <Loader2 className="w-12 h-12 animate-spin text-[#003366]" />
        <p className="text-gray-600">Loading profitability data...</p>
      </div>
    );
  }

  return (
    <div data-testid="profitability-manager" className="space-y-6">
      {/* Header */}
      <div className="flex flex-col lg:flex-row gap-4 lg:items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold text-gray-900 flex items-center gap-3" data-testid="profitability-title">
            <BarChart3 className="w-8 h-8 text-[#003366]" />
            Profitability Analysis
          </h1>
          <p className="text-gray-600 mt-1">
            Analyze profit margins, identify low-profit products, and optimize pricing
          </p>
        </div>
        <div className="flex flex-wrap gap-2">
          {missingColorsCount > 0 && (
            <Button
              variant="default"
              onClick={startBulkColorDetection}
              disabled={bulkColorStatus?.status === "running"}
              className="bg-gradient-to-r from-purple-600 to-indigo-600 hover:from-purple-700 hover:to-indigo-700"
              data-testid="bulk-ai-analyze-btn"
            >
              {bulkColorStatus?.status === "running" ? (
                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
              ) : (
                <Sparkles className="w-4 h-4 mr-2" />
              )}
              Bulk AI Analyze ({missingColorsCount})
            </Button>
          )}
          <Button
            variant="outline"
            onClick={runMigration}
            disabled={migrating}
            data-testid="run-migration-btn"
          >
            {migrating ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Zap className="w-4 h-4 mr-2" />}
            Run Migration
          </Button>
          <Button
            variant="outline"
            onClick={exportToCSV}
            data-testid="export-csv-btn"
          >
            <Download className="w-4 h-4 mr-2" />
            Export CSV
          </Button>
          <Button
            onClick={() => fetchReport()}
            disabled={refreshing}
            data-testid="refresh-report-btn"
          >
            {refreshing ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <RefreshCw className="w-4 h-4 mr-2" />}
            Refresh
          </Button>
        </div>
      </div>

      {/* Migration Result Banner */}
      {migrationResult && (
        <div className="bg-emerald-50 border border-emerald-200 rounded-xl p-4 flex items-center gap-4" data-testid="migration-result">
          <CheckCircle2 className="w-6 h-6 text-emerald-600 flex-shrink-0" />
          <div>
            <p className="font-semibold text-emerald-800">Migration Complete</p>
            <p className="text-emerald-700 text-sm">
              {migrationResult.migrated_cost_of_good} cost fields, {migrationResult.migrated_finish} finish fields, {migrationResult.migrated_sale_price} sale prices migrated
            </p>
          </div>
          <button onClick={() => setMigrationResult(null)} className="ml-auto text-emerald-600 hover:text-emerald-800">
            <X className="w-5 h-5" />
          </button>
        </div>
      )}

      {/* Summary Cards */}
      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4" data-testid="profitability-summary">
        <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-4 hover:shadow-md transition-shadow">
          <div className="flex items-center gap-2 text-gray-500 text-sm mb-1">
            <Package className="w-4 h-4" />
            Total Products
          </div>
          <p className="text-2xl font-bold text-gray-900" data-testid="total-products">{summary.total_products || 0}</p>
        </div>
        
        <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-4 hover:shadow-md transition-shadow">
          <div className="flex items-center gap-2 text-gray-500 text-sm mb-1">
            <Percent className="w-4 h-4" />
            Avg Margin
          </div>
          <p className="text-2xl font-bold text-gray-900" data-testid="avg-margin">{summary.avg_margin?.toFixed(1) || 0}%</p>
        </div>
        
        <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-4 hover:shadow-md transition-shadow">
          <div className="flex items-center gap-2 text-gray-500 text-sm mb-1">
            <DollarSign className="w-4 h-4" />
            Total Profit
          </div>
          <p className="text-2xl font-bold text-emerald-600" data-testid="total-profit">{formatCurrency(summary.total_potential_profit)}</p>
        </div>
        
        <div className="bg-white rounded-xl shadow-sm border border-orange-200 p-4 hover:shadow-md transition-shadow">
          <div className="flex items-center gap-2 text-orange-600 text-sm mb-1">
            <AlertTriangle className="w-4 h-4" />
            Low Margin (&lt;10%)
          </div>
          <p className="text-2xl font-bold text-orange-600" data-testid="low-margin-count">{summary.low_margin_count || 0}</p>
        </div>
        
        <div className="bg-white rounded-xl shadow-sm border border-red-200 p-4 hover:shadow-md transition-shadow">
          <div className="flex items-center gap-2 text-red-600 text-sm mb-1">
            <TrendingDown className="w-4 h-4" />
            Negative Profit
          </div>
          <p className="text-2xl font-bold text-red-600" data-testid="negative-count">{summary.negative_profit_count || 0}</p>
        </div>
        
        <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-4 hover:shadow-md transition-shadow">
          <div className="flex items-center gap-2 text-gray-500 text-sm mb-1">
            <AlertTriangle className="w-4 h-4" />
            Missing Data
          </div>
          <p className="text-2xl font-bold text-gray-600" data-testid="missing-data-count">{summary.missing_data_count || 0}</p>
        </div>
      </div>

      {/* Filters & Controls */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-4" data-testid="profitability-filters">
        <div className="flex flex-col xl:flex-row gap-4 xl:items-center justify-between">
          <div className="flex flex-wrap gap-3 items-center">
            {/* Search */}
            <div className="relative w-64">
              <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
              <Input
                value={searchTerm}
                onChange={(e) => { setSearchTerm(e.target.value); setPage(1); }}
                placeholder="Search products..."
                className="pl-9"
                data-testid="profitability-search"
              />
            </div>
            
            {/* Collection Filter */}
            <Select value={selectedCollection} onValueChange={(val) => { setSelectedCollection(val === "all" ? "" : val); setPage(1); }}>
              <SelectTrigger className="w-48" data-testid="collection-filter">
                <SelectValue placeholder="All Collections" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Collections</SelectItem>
                {(filters.collections || []).map(col => (
                  <SelectItem key={col} value={col}>{col}</SelectItem>
                ))}
              </SelectContent>
            </Select>
            
            {/* Category Filter */}
            <Select value={selectedCategory} onValueChange={(val) => { setSelectedCategory(val === "all" ? "" : val); setPage(1); }}>
              <SelectTrigger className="w-40" data-testid="category-filter">
                <SelectValue placeholder="All Categories" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Categories</SelectItem>
                {(filters.categories || []).map(cat => (
                  <SelectItem key={cat} value={cat}>{cat}</SelectItem>
                ))}
              </SelectContent>
            </Select>
            
            {/* Low Margin Filter */}
            <Button
              variant={lowMarginOnly ? "default" : "outline"}
              onClick={() => { setLowMarginOnly(!lowMarginOnly); setPage(1); }}
              className={lowMarginOnly ? "bg-orange-500 hover:bg-orange-600" : ""}
              data-testid="low-margin-filter"
            >
              <AlertTriangle className="w-4 h-4 mr-2" />
              Low Margin Only
            </Button>
          </div>
          
          {/* Sort Controls */}
          <div className="flex gap-2 items-center">
            <span className="text-sm text-gray-500">Sort by:</span>
            <Select value={sortBy} onValueChange={(val) => { setSortBy(val); setPage(1); }}>
              <SelectTrigger className="w-36" data-testid="sort-by-select">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="profit_margin">Margin %</SelectItem>
                <SelectItem value="profit_amount">Profit £</SelectItem>
                <SelectItem value="sale_price">Sale Price</SelectItem>
                <SelectItem value="cost_of_good">Cost</SelectItem>
                <SelectItem value="name">Name</SelectItem>
              </SelectContent>
            </Select>
            <Button
              variant="outline"
              size="icon"
              onClick={() => setSortOrder(sortOrder === "asc" ? "desc" : "asc")}
              data-testid="sort-order-btn"
            >
              <ArrowUpDown className={`w-4 h-4 transition-transform ${sortOrder === "desc" ? "rotate-180" : ""}`} />
            </Button>
          </div>
        </div>
        
        {/* Selection Actions */}
        {selectedProducts.length > 0 && (
          <div className="mt-4 pt-4 border-t border-gray-100 flex flex-wrap items-center gap-3" data-testid="selection-actions">
            <Badge variant="secondary" className="text-sm">
              {selectedProducts.length} selected
            </Badge>
            <Button size="sm" onClick={() => setBatchEditOpen(true)} data-testid="batch-edit-btn">
              <Edit3 className="w-4 h-4 mr-2" />
              Batch Edit Prices
            </Button>
            <Button size="sm" variant="outline" onClick={batchDetectColors} disabled={batchDetecting} data-testid="batch-detect-colors-btn">
              {batchDetecting ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Sparkles className="w-4 h-4 mr-2" />}
              AI Detect Colors
            </Button>
            <Button size="sm" variant="ghost" onClick={() => setSelectedProducts([])} data-testid="clear-selection-btn">
              <X className="w-4 h-4 mr-2" />
              Clear
            </Button>
          </div>
        )}
      </div>

      {/* Products Table */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-100 overflow-hidden" data-testid="profitability-table">
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="bg-gray-50 border-b border-gray-100">
              <tr>
                <th className="p-3 text-left">
                  <input
                    type="checkbox"
                    checked={products.length > 0 && products.every(p => selectedProducts.find(sp => sp.id === p.id))}
                    onChange={toggleAllVisible}
                    className="rounded"
                  />
                </th>
                <th className="p-3 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">Product</th>
                <th className="p-3 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">Collection</th>
                <th className="p-3 text-right text-xs font-semibold text-gray-600 uppercase tracking-wider">Sale Price</th>
                <th className="p-3 text-right text-xs font-semibold text-gray-600 uppercase tracking-wider">Cost</th>
                <th className="p-3 text-right text-xs font-semibold text-gray-600 uppercase tracking-wider">Profit</th>
                <th className="p-3 text-center text-xs font-semibold text-gray-600 uppercase tracking-wider">Margin</th>
                <th className="p-3 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">Finish</th>
                <th className="p-3 text-center text-xs font-semibold text-gray-600 uppercase tracking-wider">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {products.map((product, index) => {
                const isSelected = selectedProducts.find(p => p.id === product.id);
                const marginBg = getMarginColor(product.profit_margin);
                const marginLabel = getMarginLabel(product.profit_margin);
                
                return (
                  <tr 
                    key={product.id} 
                    className={`hover:bg-gray-50 transition-colors ${isSelected ? "bg-blue-50" : ""}`}
                    data-testid={`product-row-${index}`}
                  >
                    <td className="p-3">
                      <input
                        type="checkbox"
                        checked={!!isSelected}
                        onChange={() => toggleProductSelection(product)}
                        className="rounded"
                      />
                    </td>
                    <td className="p-3">
                      <div className="flex items-center gap-3">
                        <div className="w-12 h-12 rounded-lg bg-gray-100 overflow-hidden flex-shrink-0">
                          {product.image ? (
                            <img src={product.image} alt="" className="w-full h-full object-cover" />
                          ) : (
                            <div className="w-full h-full flex items-center justify-center">
                              <Package className="w-5 h-5 text-gray-400" />
                            </div>
                          )}
                        </div>
                        <div className="min-w-0">
                          <p className="font-medium text-gray-900 truncate max-w-[200px]" title={product.name}>
                            {product.name}
                          </p>
                          <p className="text-xs text-gray-500">{product.sku || "No SKU"}</p>
                        </div>
                      </div>
                    </td>
                    <td className="p-3">
                      <div>
                        <p className="text-sm text-gray-900">{product.collection_name}</p>
                        <p className="text-xs text-gray-500">{product.category}</p>
                      </div>
                    </td>
                    <td className="p-3 text-right">
                      <div>
                        <p className="font-semibold text-gray-900">{formatCurrency(product.sale_price)}</p>
                        {product.rrp_display && (
                          <p className="text-xs text-gray-400 line-through">{product.rrp_display}</p>
                        )}
                      </div>
                    </td>
                    <td className="p-3 text-right">
                      <p className="text-gray-700">{formatCurrency(product.cost_of_good)}</p>
                    </td>
                    <td className="p-3 text-right">
                      <p className={`font-semibold ${product.profit_amount < 0 ? "text-red-600" : "text-emerald-600"}`}>
                        {formatCurrency(product.profit_amount)}
                      </p>
                    </td>
                    <td className="p-3">
                      <div className="flex flex-col items-center gap-1">
                        <div className={`px-3 py-1 rounded-full text-white text-sm font-medium ${marginBg}`}>
                          {product.profit_margin !== null ? `${product.profit_margin.toFixed(1)}%` : "-"}
                        </div>
                        <span className="text-xs text-gray-500">{marginLabel}</span>
                      </div>
                    </td>
                    <td className="p-3">
                      <div className="flex items-center gap-2">
                        <Palette className="w-4 h-4 text-gray-400" />
                        <span className="text-sm text-gray-700 truncate max-w-[100px]" title={product.finish}>
                          {product.finish || "-"}
                        </span>
                      </div>
                    </td>
                    <td className="p-3">
                      <div className="flex items-center justify-center gap-1">
                        <Button
                          size="icon"
                          variant="ghost"
                          onClick={() => { setEditingProduct(product); setNewPrice(product.sale_price?.toString() || ""); }}
                          title="Edit Price"
                          data-testid={`edit-price-${index}`}
                        >
                          <Edit3 className="w-4 h-4" />
                        </Button>
                        <Button
                          size="icon"
                          variant="ghost"
                          onClick={() => detectProductColor(product)}
                          disabled={detectingColor === product.id}
                          title="AI Detect Color"
                          data-testid={`detect-color-${index}`}
                        >
                          {detectingColor === product.id ? (
                            <Loader2 className="w-4 h-4 animate-spin" />
                          ) : (
                            <Sparkles className="w-4 h-4" />
                          )}
                        </Button>
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
        
        {/* Pagination */}
        <div className="px-4 py-3 border-t border-gray-100 flex items-center justify-between">
          <p className="text-sm text-gray-600">
            Showing {((page - 1) * limit) + 1} - {Math.min(page * limit, report?.total || 0)} of {report?.total || 0}
          </p>
          <div className="flex items-center gap-2">
            <Button
              variant="outline"
              size="sm"
              disabled={page <= 1}
              onClick={() => setPage(page - 1)}
              data-testid="prev-page-btn"
            >
              <ChevronLeft className="w-4 h-4" />
            </Button>
            <span className="text-sm text-gray-600">
              Page {page} of {report?.pages || 1}
            </span>
            <Button
              variant="outline"
              size="sm"
              disabled={page >= (report?.pages || 1)}
              onClick={() => setPage(page + 1)}
              data-testid="next-page-btn"
            >
              <ChevronRight className="w-4 h-4" />
            </Button>
          </div>
        </div>
      </div>

      {/* Single Price Edit Dialog */}
      <Dialog open={!!editingProduct} onOpenChange={(open) => !open && setEditingProduct(null)}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Edit3 className="w-5 h-5" />
              Edit Sale Price
            </DialogTitle>
            <DialogDescription>
              Update the sale price for <span className="font-semibold">{editingProduct?.name}</span>
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="grid grid-cols-2 gap-4 text-sm">
              <div>
                <p className="text-gray-500">Current Price</p>
                <p className="font-semibold">{formatCurrency(editingProduct?.sale_price)}</p>
              </div>
              <div>
                <p className="text-gray-500">Cost</p>
                <p className="font-semibold">{formatCurrency(editingProduct?.cost_of_good)}</p>
              </div>
            </div>
            <div>
              <label className="text-sm font-medium mb-2 block">New Sale Price (£)</label>
              <Input
                type="number"
                step="0.01"
                value={newPrice}
                onChange={(e) => setNewPrice(e.target.value)}
                placeholder="Enter new price"
                data-testid="new-price-input"
              />
            </div>
            {newPrice && editingProduct?.cost_of_good && (
              <div className="bg-gray-50 rounded-lg p-3">
                <p className="text-sm text-gray-600">
                  New Margin: <span className="font-semibold text-emerald-600">
                    {(((parseFloat(newPrice) - editingProduct.cost_of_good) / parseFloat(newPrice)) * 100).toFixed(1)}%
                  </span>
                </p>
                <p className="text-sm text-gray-600">
                  New Profit: <span className="font-semibold text-emerald-600">
                    {formatCurrency(parseFloat(newPrice) - editingProduct.cost_of_good)}
                  </span>
                </p>
              </div>
            )}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setEditingProduct(null)}>Cancel</Button>
            <Button onClick={handlePriceUpdate} disabled={updating || !newPrice} data-testid="save-price-btn">
              {updating ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Save className="w-4 h-4 mr-2" />}
              Save
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Batch Edit Dialog */}
      <Dialog open={batchEditOpen} onOpenChange={setBatchEditOpen}>
        <DialogContent className="sm:max-w-lg">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Edit3 className="w-5 h-5" />
              Batch Price Update
            </DialogTitle>
            <DialogDescription>
              Update prices for {selectedProducts.length} selected products
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div>
              <label className="text-sm font-medium mb-2 block">Adjustment Type</label>
              <Select value={batchAdjustmentType} onValueChange={setBatchAdjustmentType}>
                <SelectTrigger data-testid="batch-adjustment-type">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="percentage">Percentage Change (+/-)</SelectItem>
                  <SelectItem value="fixed">Fixed Amount (+/-)</SelectItem>
                  <SelectItem value="set_margin">Set Target Margin (%)</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <label className="text-sm font-medium mb-2 block">
                {batchAdjustmentType === "percentage" && "Percentage (e.g., 10 for +10%, -5 for -5%)"}
                {batchAdjustmentType === "fixed" && "Amount in £ (e.g., 10 for +£10, -5 for -£5)"}
                {batchAdjustmentType === "set_margin" && "Target Margin % (e.g., 25 for 25% margin)"}
              </label>
              <Input
                type="number"
                step="0.1"
                value={batchPriceAdjustment}
                onChange={(e) => setBatchPriceAdjustment(e.target.value)}
                placeholder={batchAdjustmentType === "set_margin" ? "25" : "10"}
                data-testid="batch-adjustment-value"
              />
            </div>
            <div className="bg-gray-50 rounded-lg p-3 text-sm">
              <p className="font-medium mb-2">Preview (first 3 products):</p>
              {selectedProducts.slice(0, 3).map(p => {
                let newPrice = p.sale_price || 0;
                const adj = parseFloat(batchPriceAdjustment) || 0;
                
                if (batchAdjustmentType === "percentage") {
                  newPrice = newPrice * (1 + adj / 100);
                } else if (batchAdjustmentType === "fixed") {
                  newPrice = newPrice + adj;
                } else if (batchAdjustmentType === "set_margin" && p.cost_of_good > 0) {
                  newPrice = p.cost_of_good / (1 - adj / 100);
                }
                
                return (
                  <div key={p.id} className="flex justify-between py-1 border-b border-gray-200 last:border-0">
                    <span className="truncate max-w-[200px]">{p.name}</span>
                    <span>
                      {formatCurrency(p.sale_price)} → <span className="font-semibold text-emerald-600">{formatCurrency(Math.max(0, newPrice))}</span>
                    </span>
                  </div>
                );
              })}
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setBatchEditOpen(false)}>Cancel</Button>
            <Button onClick={handleBatchPriceUpdate} disabled={updating || !batchPriceAdjustment} data-testid="apply-batch-btn">
              {updating ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Save className="w-4 h-4 mr-2" />}
              Apply to {selectedProducts.length} Products
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Bulk AI Color Detection Dialog */}
      <Dialog open={showBulkColorDialog} onOpenChange={setShowBulkColorDialog}>
        <DialogContent className="sm:max-w-lg">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Sparkles className="w-5 h-5 text-purple-600" />
              Bulk AI Color Detection
            </DialogTitle>
            <DialogDescription>
              Automatically detecting colors for products with missing color data
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            {bulkColorStatus && (
              <>
                {/* Progress Bar */}
                <div className="space-y-2">
                  <div className="flex justify-between text-sm">
                    <span className="text-gray-600">Progress</span>
                    <span className="font-medium">{bulkColorStatus.progress_percent}%</span>
                  </div>
                  <div className="h-3 bg-gray-200 rounded-full overflow-hidden">
                    <div 
                      className="h-full bg-gradient-to-r from-purple-500 to-indigo-500 transition-all duration-500 ease-out"
                      style={{ width: `${bulkColorStatus.progress_percent}%` }}
                    />
                  </div>
                </div>

                {/* Stats */}
                <div className="grid grid-cols-3 gap-3">
                  <div className="bg-gray-50 rounded-lg p-3 text-center">
                    <p className="text-2xl font-bold text-gray-900">{bulkColorStatus.processed}</p>
                    <p className="text-xs text-gray-500">Processed</p>
                  </div>
                  <div className="bg-emerald-50 rounded-lg p-3 text-center">
                    <p className="text-2xl font-bold text-emerald-600">{bulkColorStatus.successful}</p>
                    <p className="text-xs text-emerald-700">Successful</p>
                  </div>
                  <div className="bg-red-50 rounded-lg p-3 text-center">
                    <p className="text-2xl font-bold text-red-600">{bulkColorStatus.failed}</p>
                    <p className="text-xs text-red-700">Failed</p>
                  </div>
                </div>

                {/* Status Badge */}
                <div className="flex items-center gap-2">
                  <span className="text-sm text-gray-600">Status:</span>
                  <Badge className={
                    bulkColorStatus.status === "running" ? "bg-blue-500" :
                    bulkColorStatus.status === "completed" ? "bg-emerald-500" :
                    bulkColorStatus.status === "failed" ? "bg-red-500" : "bg-gray-500"
                  }>
                    {bulkColorStatus.status === "running" && <Loader2 className="w-3 h-3 mr-1 animate-spin" />}
                    {bulkColorStatus.status?.charAt(0).toUpperCase() + bulkColorStatus.status?.slice(1)}
                  </Badge>
                </div>

                {/* Current Product */}
                {bulkColorStatus.current_product && bulkColorStatus.status === "running" && (
                  <div className="bg-purple-50 rounded-lg p-3">
                    <p className="text-xs text-purple-600 mb-1">Currently processing:</p>
                    <p className="text-sm font-medium text-purple-900 truncate">{bulkColorStatus.current_product}</p>
                  </div>
                )}

                {/* Recent Results */}
                {bulkColorStatus.results?.length > 0 && (
                  <div>
                    <p className="text-sm font-medium text-gray-700 mb-2">Recent detections:</p>
                    <div className="max-h-40 overflow-y-auto space-y-1">
                      {bulkColorStatus.results.slice(-5).reverse().map((r, i) => (
                        <div key={i} className="flex items-center justify-between text-sm bg-gray-50 px-3 py-2 rounded">
                          <span className="truncate max-w-[200px]">{r.name}</span>
                          <Badge variant="outline" className="text-xs">
                            <Palette className="w-3 h-3 mr-1" />
                            {r.detected_color}
                          </Badge>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {/* Errors */}
                {bulkColorStatus.errors?.length > 0 && (
                  <div>
                    <p className="text-sm font-medium text-red-700 mb-2">Recent errors:</p>
                    <div className="max-h-24 overflow-y-auto space-y-1">
                      {bulkColorStatus.errors.slice(-3).map((e, i) => (
                        <div key={i} className="text-xs text-red-600 bg-red-50 px-2 py-1 rounded">
                          {e.name || e.product_id}: {e.error}
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </>
            )}
          </div>
          <DialogFooter>
            <Button 
              variant="outline" 
              onClick={() => {
                setShowBulkColorDialog(false);
                if (bulkColorStatus?.status === "completed" || bulkColorStatus?.status === "failed") {
                  setBulkColorJobId(null);
                  setBulkColorStatus(null);
                }
              }}
            >
              {bulkColorStatus?.status === "running" ? "Hide" : "Close"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};
